import React from "react";
import { Link, Redirect } from "react-router-dom";
import isEqual from "react-fast-compare";
import { Box, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, } from "@material-ui/core";
import { red } from "@material-ui/core/colors";
import { Add, Edit, FileCopy, Pageview } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { BusinessOwnerLayout } from "frontend/src/pages/layouts";
import { ButtonLoader } from "frontend/src/ui/button-loader";
import { AudioControlButton } from "frontend/src/ui/audio-control-button";
import { DeleteButtonWithModal } from "frontend/src/ui/delete-button-with-modal";
import { TableRowsSkeleton } from "frontend/src/ui/table-skeleton";
import { useCopyPlaylist, useDeletePlaylist, } from "frontend/src/domain/playlists/actions";
import { useCurrentPlaylistTrackSource } from "frontend/src/domain/player-track-queue";
import { pause, resume } from "frontend/src/features/control-audio";
import { paths } from "frontend/src/pages/paths/index";
import { playPlaylistAttempted, } from "frontend/src/features/play-playlist";
import { usePageModel } from "./model";
var useRowStyles = makeStyles(function (theme) { return ({
    row: {
        backgroundColor: function (_a) {
            var isDeleteRequestPending = _a.isDeleteRequestPending, isSelectedForPlayer = _a.isSelectedForPlayer;
            if (isDeleteRequestPending)
                return red[300];
            return isSelectedForPlayer
                ? theme.palette.action.hover
                : theme.palette.background.paper;
        },
        "&:nth-of-type(odd)": {
            backgroundColor: function (_a) {
                var isSelectedForPlayer = _a.isSelectedForPlayer;
                return isSelectedForPlayer
                    ? theme.palette.action.hover
                    : theme.palette.action.selected;
            },
        },
    },
    link: {
        textDecoration: "none",
        color: "inherit",
    },
}); });
var Row = React.memo(function (_a) {
    var id = _a.id, title = _a.title, url = _a.url, trackCount = _a.trackCount, duration = _a.duration, updatedAt = _a.updatedAt, player = _a.player, permissions = _a.permissions;
    var classes = useRowStyles({
        isDeleteRequestPending: false,
        isSelectedForPlayer: player.selected,
    });
    var copy = useCopyPlaylist();
    var remove = useDeletePlaylist();
    return (React.createElement(TableRow, { className: classes.row },
        React.createElement(TableCell, { width: "5%" },
            React.createElement(AudioControlButton, { isLoading: player.loading, onPause: player.pause, onPlay: player.selected
                    ? player.play.ifSelected
                    : function () {
                        return player.play.ifNotSelected({
                            id: id,
                            title: title,
                            startAtTrackIndex: 0,
                        });
                    }, isPlaying: player.playing })),
        React.createElement(TableCell, null,
            React.createElement(Link, { className: classes.link, to: url }, title)),
        React.createElement(TableCell, null, duration),
        React.createElement(TableCell, null, trackCount),
        React.createElement(TableCell, null, updatedAt),
        React.createElement(TableCell, null,
            React.createElement(Box, { display: "inline-flex", alignItems: "center", justifyContent: "center" },
                permissions.view && (React.createElement(Tooltip, { title: "Посмотреть" },
                    React.createElement(Link, { to: url },
                        React.createElement(IconButton, { size: "small" },
                            React.createElement(Pageview, null))))),
                permissions.edit && (React.createElement(Tooltip, { title: "Редактировать" },
                    React.createElement(Link, { to: url },
                        React.createElement(IconButton, { size: "small" },
                            React.createElement(Edit, null))))),
                React.createElement(Tooltip, { title: "Копирование в «Мои плейлисты»" },
                    React.createElement(IconButton, { size: "small", onClick: function () { return copy.do(id); } },
                        React.createElement(FileCopy, null))))),
        React.createElement(TableCell, null, permissions.delete && (React.createElement(DeleteButtonWithModal, { message: "\u0412\u044B \u0443\u0432\u0435\u0440\u0435\u043D\u044B, \u0447\u0442\u043E \u0445\u043E\u0442\u0438\u0442\u0435 \u0443\u0434\u0430\u043B\u0438\u0442\u044C \"" + title + "\"?", onDelete: function () { return remove.do(id); } })))));
}, isEqual);
var Playlist = function (_a) {
    var playlists = _a.playlists;
    var source = useCurrentPlaylistTrackSource();
    return (React.createElement(React.Fragment, null, playlists.map(function (_a) {
        var id = _a.id, title = _a.title, duration = _a.duration, trackCount = _a.trackCount, updatedAt = _a.updatedAt, permissions = _a.permissions, url = _a.url;
        var selected = Boolean(source && source.attributes.id === id);
        var loading = Boolean(selected && (source === null || source === void 0 ? void 0 : source.loading));
        var playing = Boolean(selected && (source === null || source === void 0 ? void 0 : source.playing));
        return (React.createElement(Row, { key: id, id: id, title: title, trackCount: trackCount, duration: duration, updatedAt: updatedAt, url: url, permissions: permissions, player: {
                selected: selected,
                loading: loading,
                playing: playing,
                pause: pause,
                play: {
                    ifSelected: resume,
                    ifNotSelected: playPlaylistAttempted,
                },
            } }));
    })));
};
var Page = function () {
    var _a = usePageModel(), playlists = _a.playlists, type = _a.type;
    if (playlists.create.isSuccess && playlists.create.id) {
        return (React.createElement(Redirect, { to: paths.business.owner.playlists.edit(playlists.create.id) }));
    }
    return (React.createElement(Box, null,
        type.private && (React.createElement(Box, { display: "flex", justifyContent: "flex-end", mb: 2 },
            React.createElement(ButtonLoader, { isLoading: playlists.create.pending, variant: "contained", color: "primary", startIcon: React.createElement(Add, { fontSize: "small" }), onClick: playlists.create.run }, "Создать"))),
        playlists.empty && (React.createElement(Box, { component: Paper, p: 2 },
            React.createElement(Typography, null, "Пока что здесь нет ни одного плейлиста."))),
        !playlists.empty && (React.createElement(TableContainer, { component: Paper },
            React.createElement(Table, null,
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, null, null),
                        React.createElement(TableCell, null, "Название"),
                        React.createElement(TableCell, null, "Продолжительность"),
                        React.createElement(TableCell, null, "Количество треков"),
                        React.createElement(TableCell, null, "Последнее изменение"),
                        React.createElement(TableCell, null, null),
                        React.createElement(TableCell, { width: "5%" }, null))),
                React.createElement(TableBody, null, playlists.items ? (React.createElement(Playlist, { playlists: playlists.items })) : (React.createElement(TableRowsSkeleton, { rows: 15, columns: 7 }))))))));
};
export var PlaylistsPage = function () { return (React.createElement(BusinessOwnerLayout, null,
    React.createElement(Page, null))); };
